'use client'

import { FormControl } from '@/components/form-control'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Textarea } from '@/components/ui/textarea'
import { ToastAction } from '@/components/ui/toast'
import { useToast } from '@/hooks/use-toast'
import { FLAG_SALARY } from '@/modules/salary/queries'
import { useMutation } from '@apollo/client'
import { Flag } from 'lucide-react'
import { useEffect, useId, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'

const defaultFormValues = {
  reason: '',
  additionalInfo: '',
}

export function FlagSalaryButton({
  salaryId,
  className,
  onClick,
}: {
  salaryId: string
  className?: string
  onClick?: () => void
}) {
  const { toast } = useToast()
  const [isOpen, setIsOpen] = useState(false)
  const id = useId()
  const form = useForm({
    defaultValues: defaultFormValues,
  })
  const { control, watch, handleSubmit, reset, formState } = form
  const isFormValid = formState.isValid
  const reason = watch('reason')
  const additionalInfo = watch('additionalInfo')

  const [flagSalary, { loading }] = useMutation(FLAG_SALARY, {
    variables: {
      input: { salaryId, reason: `${reason}; ${additionalInfo}` },
    },
    onCompleted: () => {
      setIsOpen(false)
      toast({
        title: 'Salary flagged',
        description: 'Thank you for helping us keep our data accurate.',
        action: (
          <ToastAction
            altText="Thanks"
            className="bg-button-primary-bg text-button-primary-text"
          >
            OK
          </ToastAction>
        ),
      })
    },
    onError: (error) => {
      setIsOpen(false)
      const message = error.message || 'Something went wrong. Please try again.'
      toast({
        title: 'Failed to flag salary',
        description: message,
        action: (
          <ToastAction
            altText="Try again"
            className="bg-button-primary-bg text-button-primary-text"
          >
            OK
          </ToastAction>
        ),
      })
    },
  })

  useEffect(() => {
    if (isOpen) {
      reset(defaultFormValues)
    }
  }, [isOpen, reset])

  const submit = handleSubmit(() => {
    void flagSalary()
  })

  return (
    <FormProvider {...form}>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <Button 
            variant="ghost" 
            className="w-fit px-3" 
            id={id} 
            disabled={loading}
            onClick={() => onClick?.()}
          >
            <Flag className={className} />
            <span className="ml-1 hidden xl:block">Flag</span>
          </Button>
        </DialogTrigger>
        <DialogContent
          className="sm:max-w-md max-w-full sm:max-h-[90%] max-h-full overflow-auto"
          title="Flag Salary"
        >
          <DialogTitle>Flag Salary</DialogTitle>
          <ScrollArea className="w-full flex flex-col">
            <p className="text-copy-accent mb-4">
              The quality of our salary data is very important to us and we
              appreciate all the help we can get from our community.
            </p>
            <Controller
              control={control}
              name="additionalInfo"
              rules={{ required: 'Please provide additional information' }}
              render={({ field }) => {
                return (
                  <FormControl label="What is the issue?">
                    <Textarea
                      {...field}
                      rows={6}
                      placeholder="Type Here..."
                      maxLength={0}
                    />
                  </FormControl>
                )
              }}
            />
            <p className="text-xs font-medium text-copy-accent mt-4">
              This feedback will be shared with Marit only.
            </p>
            <p className="text-xs text-copy-muted mt-1">
              Once submitted, a member of our team will investigate the salary
              and take appropriate action.
            </p>
            <DialogFooter>
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={submit}
              isDisabled={!isFormValid}
              className="px-6"
            >
              Flag
            </Button>
          </DialogFooter>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </FormProvider>
  )
}
